import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Typography } from '@kizen/kds/Typography';
import Icon from '__components/Kizen/Icon';
import type { PreviewItem } from 'ts-components/ChatBotV2/types';

interface PreviewCardsProps {
  items: PreviewItem[];
  onSelect?: (item: PreviewItem) => void;
}

const CARD_CONFIG: Record<
  string,
  { icon: string; actionLabel: (t: TFunction) => string }
> = {
  filter: {
    icon: 'filter',
    actionLabel: (t) => t('View Filtered Records'),
  },
  custom_object: {
    icon: 'database-light',
    actionLabel: (t) => t('View Object'),
  },
};

const DEFAULT_CONFIG = CARD_CONFIG.custom_object;
const FILTER_ICON_OFFSET_CLASSNAME = 'ml-[-20px]';

export const PreviewCards: FC<PreviewCardsProps> = ({ items, onSelect }) => {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-[20px] ml-[25px] animate-fadeUp">
      {items.map((preview, index) => {
        const config = CARD_CONFIG[preview.type] ?? DEFAULT_CONFIG;
        const icon = preview.icon || config.icon;
        const iconOffsetClassName =
          icon === 'filter' ? FILTER_ICON_OFFSET_CLASSNAME : '';

        return (
          <button
            key={`${preview.type}-${preview.label}`}
            type="button"
            onClick={() => onSelect?.(preview)}
            className="flex items-center gap-[12px] w-fit max-w-[75%] text-left pl-[12px] pr-[16px] py-[10px] rounded-[10px] bg-background-white border border-solid border-gray-200 hover:border-[var(--color-ai-user-bg)] hover:bg-[var(--color-ai-system-bg)] hover:shadow-tile active:brightness-[0.96] transition-all duration-200 cursor-pointer focus:outline-none focus-visible:outline-none"
            data-qa-id={`preview-card-${index}`}
          >
            <div className="flex items-center justify-center w-[32px] h-[32px] rounded-[5px] bg-[var(--color-ai-system-bg)] flex-shrink-0">
              <div className={iconOffsetClassName}>
                <Icon icon={icon} color="ai-user-bg" />
              </div>
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-[10px]">
              <Typography
                size="md"
                weight="bold"
                color="ai-user-bg"
                truncate
                enableTooltip
              >
                {preview.label}
              </Typography>
              <Typography size="xs" color="font-secondary">
                {config.actionLabel(t)}
              </Typography>
            </div>
            <div className="flex-shrink-0 ml-[4px]">
              <Icon icon="external-link" color="ai-user-bg" size="sm" />
            </div>
          </button>
        );
      })}
    </div>
  );
};
